import React, { useRef } from 'react';
import { useFocusRing } from '@react-aria/focus';
import { useHover } from '@react-aria/interactions';
import clsx from 'clsx';

export interface BaseCardProps {
    /** The content to display inside the card */
    children: React.ReactNode;
    /** Visual variant of the card */
    variant?: 'default' | 'elevated' | 'outlined' | 'ghost';
    /** Additional CSS classes to apply */
    className?: string;
    /** Whether the card is disabled */
    isDisabled?: boolean;
    /** Whether to show hover effects */
    hoverable?: boolean;
    /** Optional aria-label for accessibility */
    'aria-label'?: string;
    /** Optional aria-describedby for accessibility */
    'aria-describedby'?: string;
    /** Click handler for the card */
    onClick?: () => void;
    /** Custom styles */
    style?: React.CSSProperties;
}

export const BaseCard = React.forwardRef<HTMLDivElement, BaseCardProps>(
    (
        {
            children,
            variant = 'default',
            className,
            isDisabled = false,
            hoverable = true,
            'aria-label': ariaLabel,
            'aria-describedby': ariaDescribedBy,
            onClick,
            style,
        },
        forwardedRef
    ) => {
        const internalRef = useRef<HTMLDivElement>(null);

        // Sync forwarded ref with internal ref
        React.useImperativeHandle(forwardedRef, () => internalRef.current!);

        const { focusProps, isFocusVisible } = useFocusRing();
        const { hoverProps, isHovered } = useHover({ isDisabled });

        const isInteractive = Boolean(onClick);

        // Variant styles
        const variantStyles = {
            default: 'bg-white border border-gray-200 shadow-sm',
            elevated: 'bg-white border border-gray-200 shadow-md',
            outlined: 'bg-white border-2 border-gray-300',
            ghost: 'bg-transparent border border-transparent',
        };

        const handleClick = () => {
            if (!isDisabled && onClick) {
                onClick();
            }
        };

        const handleKeyDown = (e: React.KeyboardEvent) => {
            if (!isDisabled && onClick && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                onClick();
            }
        };

        return (
            <div
                ref={internalRef}
                className={clsx(
                    'p-4 rounded-lg transition-all duration-200',
                    variantStyles[variant],
                    {
                        // Hover effects
                        'hover:shadow-md': hoverable && !isDisabled && variant === 'default',
                        'hover:shadow-lg': hoverable && !isDisabled && variant === 'elevated',
                        'hover:border-gray-400': hoverable && !isDisabled && variant === 'outlined',
                        'hover:bg-gray-50': hoverable && !isDisabled && variant === 'ghost',

                        // Interactive styles
                        'cursor-pointer': isInteractive && !isDisabled,

                        // Focus styles
                        'focus:outline-none': isInteractive,
                        'ring-2 ring-blue-500 ring-offset-2': isFocusVisible,

                        // Disabled styles
                        'opacity-50 cursor-not-allowed': isDisabled,
                    },
                    className
                )}
                onClick={handleClick}
                onKeyDown={handleKeyDown}
                tabIndex={isInteractive && !isDisabled ? 0 : undefined}
                role={isInteractive ? 'button' : undefined}
                aria-label={ariaLabel}
                aria-describedby={ariaDescribedBy}
                aria-disabled={isDisabled}
                style={style}
                {...(isInteractive ? { ...hoverProps, ...focusProps } : {})}
            >
                {children}
            </div>
        );
    }
);

BaseCard.displayName = 'BaseCard';